/**
 * productUtils — shared helpers for product detail page.
 *
 * These were previously inlined inside SingleProductDetail.js,
 * making them hard to test and reuse.
 */

export const COLOR_SWATCHES = {
    black:         '#1a1a1a',
    white:         '#f5f5f5',
    blue:          '#3b82f6',
    navy:          '#1e3a5f',
    red:           '#ef4444',
    green:         '#22c55e',
    yellow:        '#eab308',
    orange:        '#f97316',
    pink:          '#ec4899',
    purple:        '#a855f7',
    grey:          '#9ca3af',
    gray:          '#9ca3af',
    brown:         '#92400e',
    beige:         '#d4b896',
    'multi-color': 'linear-gradient(135deg, #ef4444 0%, #3b82f6 50%, #22c55e 100%)',
};

export const getColorSwatch = (colorName) =>
    COLOR_SWATCHES[colorName?.toLowerCase()?.trim()] ?? null;

export const getVariantStock = (variant) =>
    Number(variant?.stock_on_hand) || Number(variant?.available_stock) || 0;

/**
 * Parse the cf_productimages custom field, which Zoho sometimes returns
 * as a slightly-malformed JSON string like "[url1][url2]" or proper JSON.
 */
export const parseProductImages = (raw) => {
    if (!raw) return [];
    try {
        const fixed = String(raw).replace(/\]\s*\[/g, ',');
        const parsed = JSON.parse(fixed);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
        if (typeof raw === 'string' && raw.startsWith('http')) return [raw];
        return [];
    }
};

/**
 * Resolve the image to show for a variant. Falls back through:
 *   1. cf_singleimage (URL or JSON-wrapped URL/array)
 *   2. image_name
 *   3. first product image
 */
export const resolveVariantImage = (variant, baseImages) => {
    if (!variant) return baseImages?.[0] ?? null;
    const cfSingle = variant.custom_field_hash?.cf_singleimage;
    if (cfSingle) {
        if (typeof cfSingle === 'string' && cfSingle.startsWith('http')) return cfSingle;
        try {
            const parsed = JSON.parse(cfSingle);
            if (Array.isArray(parsed) && parsed[0]) return parsed[0];
            if (typeof parsed === 'string') return parsed;
        } catch { /* ignore */ }
    }
    if (variant.image_name) return variant.image_name;
    return baseImages?.[0] ?? null;
};

/**
 * Resolve thumb for a variant in a list — slightly different fallback:
 * prefers cf_singleimage → first cf_productimages → image_name.
 */
export const resolveVariantThumb = (variant) => {
    if (!variant) return null;
    const cf = variant.custom_field_hash?.cf_singleimage;
    if (cf) {
        try {
            const raw = String(cf).trim();
            if (raw.startsWith('http')) return raw;
            const p = JSON.parse(raw);
            if (Array.isArray(p) && p[0]) return p[0];
            if (typeof p === 'string') return p;
        } catch { /* ignore */ }
    }
    const imgs = parseProductImages(variant.custom_field_hash?.cf_productimages);
    if (imgs[0]) return imgs[0];
    return variant.image_name ?? null;
};

/**
 * Build the quantity options dropdown for a variant.
 * Returns array of values from MOQ to stock, stepping by MOQ.
 */
export const buildQuantityOptions = (moq, stock) => {
    const m = Math.max(1, Number(moq) || 1);
    const s = Math.max(0, Number(stock) || 0);
    if (s <= 0) return [];
    const opts = [];
    let q = m;
    while (q <= s) {
        opts.push(q);
        q += m;
    }
    return opts;
};

/**
 * Get the attribute axes of a variant group.
 * Zoho assigns color/size to either attribute_id1 or attribute_id2 — we
 * need to know which slot holds which axis.
 */
export const getVariantAxes = (itemGroup) => {
    const attr1 = itemGroup?.attribute_name1?.toLowerCase() ?? '';
    const attr2 = itemGroup?.attribute_name2?.toLowerCase() ?? '';
    const colorAttr = attr1 === 'color' ? 1 : attr2 === 'color' ? 2 : null;
    const sizeAttr  = colorAttr === 1 ? 2 : colorAttr === 2 ? 1 : null;
    return { colorAttr, sizeAttr };
};

export const getVariantColor = (item, colorAttr) => {
    if (colorAttr === 1) return item.attribute_option_name1;
    if (colorAttr === 2) return item.attribute_option_name2;
    return null;
};

export const getVariantSize = (item, sizeAttr) => {
    if (sizeAttr === 1) return item.attribute_option_name1;
    if (sizeAttr === 2) return item.attribute_option_name2;
    return item.attribute_option_name1 ?? item.attribute_option_name2 ?? null;
};

/**
 * Build the WhatsApp enquiry URL.
 * The phone number should come from env, not be hardcoded. If you
 * later need per-vendor phone numbers, this is where to wire that in.
 */
export const buildWhatsAppUrl = ({ phone, productName, productUrl }) => {
    const name = productName?.toLowerCase() || 'this product';
    const msg = `Hi, I am interested in ${name}. ${productUrl}`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
};